// =============================================================================
// MyOutreach — the Email Campaigns tab inside /my-profile
// =============================================================================
// Jake, 2026-09-06: "It should look just like the report page we see on the
// back end, except it should not show cold, unopened, or bounced. It should
// also not contain an edit or pause or whatever buttons - read only only."
//
// The Report tab's shape, rendered by the SHARED CampaignReport component with
// audience="player", fed by GET /api/me/outreach/:campaignId — the player
// rollup. 🔴 THE BROWSER COMPUTES NOTHING and HIDES NOTHING: every number is
// the server's, and the server never sends this page a cold, unopened or
// bounced coach (the exclusion lives in the rollup builder). No masthead
// actions, no status badge, no next-action text, no operator name.
//
// Same rule as the staff page: EVERY FILTER CHANGE RE-FETCHES WITH THE QUERY.
// The server's filter_values come from the unfiltered set so chips never
// vanish mid-drilldown; a client that filtered locally would disagree with it.
//
// Under the PLAYER masthead — the same one /my-profile uses — not the Front
// Office header and not the board's back-to-FluxEngine button. Recruit-direct
// voice on every string.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import usePendingPoll, { isSnapshotPending } from '../lib/usePendingPoll.js';
import CampaignReport from '../components/flux/CampaignReport.jsx';

export default function MyOutreach() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCampaignId = searchParams.get('campaign');
  const [campaigns, setCampaigns] = useState(null);
  const [campaignError, setCampaignError] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const [filters, setFilters] = useState({ division: [], state: [], region: [] });
  const [divisionTab, setDivisionTab] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/me/outreach')
      .then((body) => {
        if (!alive) return;
        setCampaigns(body.campaigns || []);
        setCampaignError(null);
      })
      .catch((err) => {
        if (alive) setCampaignError(err.body?.error || err.message);
      });
    return () => { alive = false; };
  }, []);

  const campaignId = useMemo(() => {
    if (!campaigns?.length) return null;
    if (requestedCampaignId && campaigns.some((campaign) => campaign.id === requestedCampaignId)) {
      return requestedCampaignId;
    }
    return campaigns[0].id;
  }, [campaigns, requestedCampaignId]);

  const filterQuery = useMemo(() => {
    const parts = [];
    for (const key of ['division', 'state', 'region']) {
      if (filters[key].length) parts.push(`${key}=${encodeURIComponent(filters[key].join(','))}`);
    }
    return parts.length ? `?${parts.join('&')}` : '';
  }, [filters]);

  useEffect(() => {
    if (!campaignId) {
      setData(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    api.get(`/api/me/outreach/${campaignId}${filterQuery}`)
      .then((body) => { if (alive) { setData(body); setError(null); } })
      .catch((err) => { if (alive) setError(err.body?.error || err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [campaignId, filterQuery, pollTick]);

  const bumpPoll = useCallback(() => setPollTick((n) => n + 1), []);
  usePendingPoll(data, bumpPoll);

  function chooseCampaign(id) {
    setFilters({ division: [], state: [], region: [] });
    setDivisionTab(null);
    setData(null);
    setSearchParams({ tab: 'email-campaigns', campaign: id });
  }

  function toggleFilter(key, value) {
    setFilters((prev) => {
      const cur = prev[key] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  }

  const tiers = data?.tiers || { hot: 0, interested: 0, total: 0, tiered_total: 0 };
  const divisions = data?.by_division || [];
  const shownDivisions = divisionTab ? divisions.filter((d) => d.division === divisionTab) : divisions;
  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-2xl text-silver-text">Email Campaigns</h1>
          <p className="text-sm text-silver-mute mt-1">See which college coaches are engaging with your outreach.</p>
        </div>
        {loading ? <span className="text-xs text-silver-mute">Updating…</span> : null}
      </div>

      {campaignError && <div className="card text-sm text-red-700 mt-6">{campaignError}</div>}
      {!campaignError && campaigns === null && (
        <div className="text-center text-silver-mute py-12">Loading your campaigns…</div>
      )}
      {campaigns?.length === 0 && (
        <div className="card text-sm text-silver-text mt-6">Your CCF coach hasn&apos;t started an email campaign for you yet.</div>
      )}

      {campaigns?.length > 0 && (
        <>
          <section className="card mt-6">
            <label className="label-dark" htmlFor="player-campaign">Campaign</label>
            <select id="player-campaign" className="input mt-1" value={campaignId || ''} onChange={(event) => chooseCampaign(event.target.value)}>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </select>
          </section>

          {error && <div className="card text-sm text-red-700 mt-6">{error}</div>}

          {!data && !error && (
            <div className="text-center text-silver-mute py-12">Loading your report…</div>
          )}

          {data && (
            <section aria-labelledby="campaign-report-heading">
            <h2 id="campaign-report-heading" className="font-display font-black text-2xl text-silver-text mt-7 break-words">
              {data.campaign?.name}
            </h2>
            <p className="text-sm text-silver-mute mt-1">
              {data.campaign?.status_label}
            </p>

            <CampaignReport
              audience="player"
              stats={data.stats}
              rollup={data}
              rollupStatus={isSnapshotPending(data) ? 'pending' : 'ready'}
              tiers={tiers}
              shownDivisions={shownDivisions}
              filters={filters}
              toggleFilter={toggleFilter}
              onReset={() => { setFilters({ division: [], state: [], region: [] }); setDivisionTab(null); }}
              divisionTab={divisionTab}
              setDivisionTab={setDivisionTab}
            />

            {!isSnapshotPending(data) && tiers.total === 0 && (
              <div className="card text-sm text-silver-text">
                No coaches have reached Interested or Hot yet. Positive interest will appear here when the report has enough defensible activity.
              </div>
            )}
            </section>
          )}
        </>
      )}
    </main>
  );
}
